import {
  Dialog,
  DialogMaxWidth,
  Grid,
  InfoIcon,
  Text,
  TextHighlight,
  TextSize
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import {
  DisplayLogicDraft,
  DisplayLogicRow
} from '../../../data/model/audienceQuestionLogic'
import { Operator } from '../../../data/model/common'
import AudienceConditionRows, { emptyRow } from '../AudienceConditionRows'
import useStyles from '../audienceConditionRows.styles'
import { SourceQuestion } from '../audienceConditions'
import { isDraftComplete } from './screenerDisplayLogic.utils'

interface Props {
  open: boolean
  questionLabel: string
  isTargeting: boolean
  draft: DisplayLogicDraft
  questions: SourceQuestion[]
  onSave: (draft: DisplayLogicDraft) => void
  onClose: () => void
}

const DialogScreenerDisplayLogic: React.FC<Props> = ({
  open,
  questionLabel,
  isTargeting,
  draft,
  questions,
  onSave,
  onClose
}: Props) => {
  const { classes } = useStyles()
  const [rows, setRows] = useState<DisplayLogicRow[]>(
    draft.rows.length > 0 ? draft.rows : [emptyRow(false)]
  )
  const [operator, setOperator] = useState<Operator>(draft.operator)
  const working: DisplayLogicDraft = { rows, operator }

  return (
    <Dialog
      open={open}
      title={`Display logic — ${questionLabel}`}
      maxWidth={DialogMaxWidth.Md}
      primaryButtonText="Save"
      primaryButtonDisabled={!isDraftComplete(working)}
      primaryButtonClick={() => onSave(working)}
      onClose={onClose}
      fullWidth
    >
      <Grid>
        <Grid
          container
          alignItems="center"
          gap="4px"
          className={classes.subtitleContainer}
        >
          <Text size={TextSize.m} highlight={TextHighlight.Background}>
            Only show this question if:
          </Text>
          <InfoIcon text="A question that is not shown records no answer and takes no part in qualification." />
        </Grid>

        {questions.length === 0 ? (
          <Text size={TextSize.m} highlight={TextHighlight.Background}>
            {`There are no questions above ${questionLabel} in the screener to use as a source.`}
          </Text>
        ) : (
          <AudienceConditionRows
            rows={rows}
            questions={questions}
            onChange={setRows}
            operator={operator}
            onOperatorChange={setOperator}
            ariaPrefix="Display logic"
            defaultNegated={false}
          />
        )}

        <Grid className={classes.warningText}>
          <Text size={TextSize.s} highlight={TextHighlight.Background}>
            {`Only questions above ${questionLabel} in the screener can be used — a later one has no answer yet when ${questionLabel} is reached.`}
          </Text>
        </Grid>

        {isTargeting && (
          <Grid className={classes.warningText}>
            <Text size={TextSize.s} highlight={TextHighlight.Background}>
              This is a targeting question, so the panel has already screened on
              it. Display logic only controls whether it is asked again
              in-survey; it does not change who arrives.
            </Text>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogScreenerDisplayLogic
